import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.tsx'
import { api, ApiError } from '../api.ts'
import { money, serviceStatusLabel, serviceTypeLabel, claimStatusLabel, warrantyStatus, dateTime } from '../format.ts'

interface Part {
  id: string
  name: string
  quantity: string
  unitPriceNet: string
  isWarranty: boolean
  lineTotalNet: string | null
}
interface Ticket {
  id: string
  number: string
  customerId: string
  customerName: string
  unitId: string | null
  serialNumber: string | null
  machineDescription: string | null
  model: string | null
  manufacturerName: string | null
  reportedAt: string
  serviceType: string
  priority: string
  status: string
  claimStatus: string
  technicianCode: string | null
  faultDescription: string | null
  workDone: string | null
  labourHours: string | null
  labourRateNet: string | null
  labourIsWarranty: boolean
  completedAt: string | null
  warrantyEnd: string | null
  warrantyActive: boolean
  supplierWarrantyEnd: string | null
  supplierWarrantyActive: boolean
  atOurRisk: boolean
  totalNet: string | null
  warrantyCostNet: string | null
  billableNet: string | null
  parts: Part[]
}

const STATUS_OPTIONS = [
  ['przyjete', 'Przyjęte'],
  ['w_trakcie', 'W trakcie'],
  ['oczekiwanie_na_czesci', 'Oczekiwanie na części'],
  ['zakonczone', 'Zakończone'],
]

export function ServiceTicketPage() {
  const { user } = useAuth()
  // Sales reads tickets; only service and the owner write them (grants 0015).
  const tylkoPodglad = user?.role === 'handlowiec'
  const { id } = useParams<{ id: string }>()
  const [t, setT] = useState<Ticket | null>(null)
  const [error, setError] = useState<string | null>(null)

  function reload() {
    if (!id) return
    api.get<{ ticket: Ticket }>(`/api/service-tickets/${id}`).then((r) => setT(r.ticket)).catch((e) => setError(e instanceof Error ? e.message : 'Błąd'))
  }
  useEffect(reload, [id])

  async function patch(body: Record<string, unknown>) {
    setError(null)
    try {
      await api.patch(`/api/service-tickets/${id}`, body)
      reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  if (error && !t) return <div className="p-6 text-red-700">{error}</div>
  if (!t) return <div className="p-6 text-slate-400">Ładowanie…</div>

  const zakonczone = t.status === 'zakonczone'
  const w = warrantyStatus(t.warrantyEnd)

  return (
    <div className="p-6 max-w-3xl">
      <Link to="/serwis" className="text-sm text-slate-500 hover:text-slate-700">← Serwis</Link>

      <div className="mt-2 mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">{t.number}</h1>
          <div className="text-sm text-slate-500 mt-0.5">
            <Link to={`/klienci/${t.customerId}`} className="hover:underline">{t.customerName}</Link>
            {' · '}
            {t.model ?? t.machineDescription}
            {t.serialNumber && ` · ${t.serialNumber}`}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            zgłoszone {dateTime(t.reportedAt)}
            {t.technicianCode && ` · technik ${t.technicianCode}`}
          </div>
        </div>
        <a href={`/api/service-tickets/${t.id}/protokol`} target="_blank" rel="noreferrer" className="text-sm border border-slate-300 text-slate-700 px-3 py-1.5 rounded-md hover:border-brand-600 shrink-0">
          Protokół PDF
        </a>
      </div>

      {tylkoPodglad && (
        <div className="mb-4 text-sm text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-3 py-2">
          Podgląd — zmiany w zgłoszeniu wprowadza serwis lub właściciel.
        </div>
      )}

      {/* Warranty indicator (SPEC 5.5) */}
      {t.unitId && (
        <div className={`mb-4 rounded-lg p-3 ${w.active ? 'bg-green-50 border border-green-200' : 'bg-slate-100 border border-slate-200'}`}>
          <div className={`text-base font-bold ${w.active ? 'text-green-800' : 'text-slate-600'}`}>{w.label}</div>
          {t.atOurRisk && (
            <div className="text-xs text-amber-700 mt-0.5">
              ⚠ Klient w gwarancji, ale gwarancja producenta już wygasła
              {t.supplierWarrantyEnd && ` (${new Date(t.supplierWarrantyEnd).toLocaleDateString('pl-PL')})`} — koszt naprawy pokryjemy sami.
            </div>
          )}
          {w.active && !t.atOurRisk && t.supplierWarrantyActive && (
            <div className="text-xs text-green-700 mt-0.5">Gwarancja producenta również aktywna — można złożyć roszczenie.</div>
          )}
        </div>
      )}

      {error && <div role="alert" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      {/* Status + type + claim */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <label className="text-xs text-slate-500">
          Status
          <select value={t.status} onChange={(e) => void patch({ status: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
            {STATUS_OPTIONS.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
          </select>
        </label>
        <label className="text-xs text-slate-500">
          Rodzaj
          <select value={t.serviceType} disabled={zakonczone} onChange={(e) => void patch({ serviceType: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white disabled:bg-slate-50">
            <option value="gwarancyjny">Gwarancyjny</option>
            <option value="pogwarancyjny">Pogwarancyjny</option>
            <option value="przeglad">Przegląd</option>
          </select>
        </label>
        <label className="text-xs text-slate-500">
          Reklamacja u producenta
          <select value={t.claimStatus} onChange={(e) => void patch({ claimStatus: e.target.value })} className="mt-0.5 w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
            <option value="brak">Bez reklamacji</option>
            <option value="zlozone">Złożona</option>
            <option value="uznane">Uznana</option>
            <option value="odrzucone">Odrzucona</option>
          </select>
        </label>
      </div>

      {/* Fault + work */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 space-y-3">
        <TextField label="Usterka (od klienta)" value={t.faultDescription} disabled={zakonczone} onSave={(v) => patch({ faultDescription: v })} />
        <TextField label="Wykonane czynności" value={t.workDone} disabled={zakonczone} onSave={(v) => patch({ workDone: v })} />
      </div>

      {/* Parts */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Części</h2>
        {t.parts.length > 0 && (
          <ul className="mb-3 divide-y divide-slate-100">
            {t.parts.map((p) => (
              <li key={p.id} className="py-1.5 flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0">
                  <span className="text-slate-700">{p.name}</span>
                  <span className="text-xs text-slate-400"> · {Number(p.quantity)} × {money(p.unitPriceNet)}</span>
                  {p.isWarranty && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">gwarancja</span>}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="tabular text-slate-600">{money(p.lineTotalNet)}</span>
                  {!zakonczone && (
                    <button onClick={() => void api.delete(`/api/service-parts/${p.id}`).then(reload)} className="text-xs text-slate-300 hover:text-red-600">usuń</button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {!zakonczone && <AddPart ticketId={t.id} defaultWarranty={t.serviceType === 'gwarancyjny'} onAdded={reload} />}
      </div>

      {/* Labour */}
      {!zakonczone && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Robocizna</h2>
          <Labour ticket={t} onSave={patch} />
        </div>
      )}

      {/* Cost split */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <div className="max-w-sm ml-auto text-sm space-y-1">
          <Row label="Koszt gwarancyjny (nasz / roszczenie)" value={money(t.warrantyCostNet)} />
          <Row label="Do faktury klienta" value={money(t.billableNet)} strong />
          <Row label="Razem" value={money(t.totalNet)} />
        </div>
      </div>

      {/* Complete / reopen */}
      <div className="flex justify-end">
        {zakonczone ? (
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">zakończone {t.completedAt && dateTime(t.completedAt)}</span>
            <button onClick={() => void patch({ status: 'w_trakcie' })} className="text-sm border border-slate-300 text-slate-600 px-4 py-2 rounded-md hover:border-brand-600">
              Wznów zgłoszenie
            </button>
          </div>
        ) : (
          <button onClick={() => void patch({ status: 'zakonczone' })} className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 font-medium">
            Zakończ zgłoszenie
          </button>
        )}
      </div>
    </div>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-6">
      <span className="text-slate-500">{label}</span>
      <span className={`tabular ${strong ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>{value}</span>
    </div>
  )
}

function TextField({ label, value, disabled, onSave }: { label: string; value: string | null; disabled?: boolean; onSave: (v: string) => void }) {
  const [v, setV] = useState(value ?? '')
  useEffect(() => setV(value ?? ''), [value])
  return (
    <label className="block text-xs text-slate-500">
      {label}
      <textarea
        value={v}
        disabled={disabled}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== (value ?? '') && onSave(v)}
        rows={2}
        className="mt-0.5 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-brand-600 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </label>
  )
}

function AddPart({ ticketId, defaultWarranty, onAdded }: { ticketId: string; defaultWarranty: boolean; onAdded: () => void }) {
  const [name, setName] = useState('')
  const [qty, setQty] = useState('1')
  const [price, setPrice] = useState('')
  const [warranty, setWarranty] = useState(defaultWarranty)
  const [error, setError] = useState<string | null>(null)

  async function add() {
    if (!name.trim() || !price) return
    setError(null)
    try {
      await api.post(`/api/service-tickets/${ticketId}/parts`, { name: name.trim(), quantity: Number(qty), unitPriceNet: Number(price), isWarranty: warranty })
      setName(''); setQty('1'); setPrice('')
      onAdded()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-end">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nazwa części" className="flex-1 min-w-40 px-3 py-1.5 border border-slate-300 rounded-md text-sm" />
        <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} title="Ilość (ujemna = zwrot)" className="w-20 px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
        <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="cena" className="w-24 px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
        <label className="flex items-center gap-1 text-xs text-slate-500">
          <input type="checkbox" checked={warranty} onChange={(e) => setWarranty(e.target.checked)} /> gwarancja
        </label>
        <button onClick={() => void add()} className="text-sm bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md hover:bg-slate-200">Dodaj</button>
      </div>
      {error && <div className="text-sm text-red-700 mt-1">{error}</div>}
    </div>
  )
}

function Labour({ ticket, onSave }: { ticket: Ticket; onSave: (b: Record<string, unknown>) => void }) {
  const [hours, setHours] = useState(ticket.labourHours ?? '')
  const [rate, setRate] = useState(ticket.labourRateNet ?? '')
  const [warranty, setWarranty] = useState(ticket.labourIsWarranty)

  // Hours and rate live or die together (the table's CHECK pairs them), so a
  // blur saves both — or, while one is still empty, nothing yet.
  function save() {
    const h = hours === '' ? null : Number(hours)
    const r = rate === '' ? null : Number(rate)
    if ((h == null) !== (r == null)) return
    if (String(h ?? '') === String(ticket.labourHours ?? '') && String(r ?? '') === String(ticket.labourRateNet ?? '')) return
    onSave({ labourHours: h, labourRateNet: r })
  }
  const polowa = (hours === '') !== (rate === '')

  return (
    <div className="flex flex-wrap gap-2 items-end text-sm">
      <label className="text-xs text-slate-500">
        Godziny
        <input type="number" min="0" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)} onBlur={save} className="mt-0.5 w-24 block px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
      </label>
      <label className="text-xs text-slate-500">
        Stawka netto/h
        <input type="number" min="0" step="1" value={rate} onChange={(e) => setRate(e.target.value)} onBlur={save} className="mt-0.5 w-28 block px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
      </label>
      {polowa && <span className="text-xs text-amber-700 pb-2">wpisz godziny i stawkę — zapisze się, gdy oba będą uzupełnione</span>}
      <label className="flex items-center gap-1 text-xs text-slate-500 pb-2">
        <input type="checkbox" checked={warranty} onChange={(e) => { setWarranty(e.target.checked); onSave({ labourIsWarranty: e.target.checked }) }} /> gwarancyjna
      </label>
    </div>
  )
}
